// ============================================================================
// OCCUPANCY STRIP — rolling free-inventory heads-up display
// ============================================================================
// Sits under the Booking Worksheet's QUICK VIEWS row. Not a view mode: it is
// always on, so an agent taking an enquiry never leaves the grid to answer
// "do I have rooms on Saturday?".
//
// It leads with FREE ROOMS and FREE %, not occupancy. Management's intent is
// that the agent sees unsold inventory and works it, and an occupancy-first
// display reads "83% — healthy" on precisely the day that needs selling.
// The colour scale is the shared availability scale (availability.colors.ts),
// which is already free-%-keyed, so green means the same thing here and on the
// Availability Calendar: there is stock to sell.
//
// Design: crs-doc/CRS-Booking-Worksheet-Day-View-Design.md §5.
//
// One unit caution, carried into the layout: the hero block is ROOMS while the
// footer line is BOOKINGS (a group booking is one arrival across five rooms).
// They are visually separated for that reason — never read as one arithmetic.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { availabilityService } from 'api/services/availabilityService';
import type { OccupancyHorizonDay } from 'api/types/availability.types';
import OccupancyDayCell from './OccupancyDayCell';
import { parseDayKey, todayKey, addDays } from 'utils/calendarDate';

/** Horizon lengths offered. 14 is the default: two weekends, no scrolling at 1440px. */
const HORIZONS = [7, 14, 30] as const;
type Horizon = (typeof HORIZONS)[number];

const HORIZON_KEY = 'crs.bookings.strip.horizon';
const COLLAPSED_KEY = 'crs.bookings.strip.collapsed';

interface OccupancyStripProps {
  /** From the worksheet's filter panel — one property control per screen. */
  propertyId: string;
  /** Currently focused day (yyyy-MM-dd), or '' when none. */
  selectedDate: string;
  /** A cell was activated. The worksheet decides what that means. */
  onSelectDate: (isoDate: string) => void;
}

const OccupancyStrip: React.FC<OccupancyStripProps> = ({
  propertyId, selectedDate, onSelectDate,
}) => {
  const [horizon, setHorizon] = useState<Horizon>(() => {
    const raw = Number(localStorage.getItem(HORIZON_KEY));
    return (HORIZONS as readonly number[]).includes(raw) ? (raw as Horizon) : 14;
  });
  const [collapsed, setCollapsed] = useState<boolean>(
    () => localStorage.getItem(COLLAPSED_KEY) === '1',
  );
  const [from, setFrom] = useState<string>(todayKey);
  const [days, setDays] = useState<OccupancyHorizonDay[]>([]);
  const [highVacancyCount, setHighVacancyCount] = useState(0);
  const [threshold, setThreshold] = useState(50);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => { localStorage.setItem(HORIZON_KEY, String(horizon)); }, [horizon]);
  useEffect(() => { localStorage.setItem(COLLAPSED_KEY, collapsed ? '1' : '0'); }, [collapsed]);

  const load = useCallback(async () => {
    if (!propertyId) { setDays([]); return; }
    setLoading(true);
    setError('');
    try {
      const res = await availabilityService.getOccupancyHorizon(propertyId, from, horizon);
      const data = res.data;
      setDays(data?.days ?? []);
      setHighVacancyCount(data?.highVacancyDayCount ?? 0);
      // Threshold comes from the server so the counter and the API agree.
      if (typeof data?.highVacancyThresholdPercent === 'number') {
        setThreshold(data.highVacancyThresholdPercent);
      }
    } catch {
      setDays([]);
      setError('Could not load the inventory position.');
    } finally {
      setLoading(false);
    }
  }, [propertyId, from, horizon]);

  useEffect(() => { if (!collapsed) load(); }, [collapsed, load]);

  /**
   * Exposed so the worksheet can refresh the strip after any row action. A
   * cancellation must move free rooms 6 → 7 here, not just remove a grid row —
   * a stale HUD is worse than none, because the agent quotes from it.
   */
  useEffect(() => {
    const handler = () => { if (!collapsed) load(); };
    window.addEventListener('crs:bookings-changed', handler);
    return () => window.removeEventListener('crs:bookings-changed', handler);
  }, [collapsed, load]);

  const isToday = from === todayKey();

  const shiftWindow = useCallback((direction: -1 | 1) => {
    setFrom(addDays(from, direction * horizon));
  }, [from, horizon]);

  const label = useMemo(() => {
    if (isToday) return `Unsold — next ${horizon} days`;
    const start = parseDayKey(from);
    const end = parseDayKey(addDays(from, horizon - 1));
    const fmt = (d: Date) =>
      d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });
    return `Unsold — ${fmt(start)} to ${fmt(end)}`;
  }, [isToday, from, horizon]);

  // ---------------------------------------------------------------- render
  const shell: React.CSSProperties = {
    padding: '6px 1.25rem 0',
  };

  const headRow: React.CSSProperties = {
    display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap',
    marginBottom: collapsed ? 0 : '0.4rem',
  };

  const miniBtn: React.CSSProperties = {
    display: 'inline-flex', alignItems: 'center', gap: '0.25rem',
    padding: '0.15rem 0.5rem', borderRadius: '0.375rem',
    border: '1px solid var(--border-color)', background: 'var(--bg-primary)',
    color: 'var(--text-secondary)', fontSize: '0.7rem', fontWeight: 600,
    cursor: 'pointer', fontFamily: 'inherit',
  };

  return (
    <div style={shell}>
      <div style={headRow}>
        <button
          type="button"
          onClick={() => setCollapsed((c) => !c)}
          style={{ ...miniBtn, border: 'none', background: 'transparent', paddingLeft: 0 }}
          aria-expanded={!collapsed}
          title={collapsed ? 'Show the inventory strip' : 'Hide the inventory strip'}
        >
          {collapsed ? <IconSystem.chevronRight size={12} /> : <IconSystem.chevronDown size={12} />}
          <span style={{
            fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.05em',
            textTransform: 'uppercase', color: 'var(--text-muted)',
          }}>
            {label}
          </span>
        </button>

        {!collapsed && highVacancyCount > 0 && (
          <span
            title={`${highVacancyCount} day(s) with more than ${threshold}% of rooms unsold`}
            style={{
              fontSize: '0.65rem', fontWeight: 700, padding: '0.1rem 0.45rem',
              borderRadius: '999px', border: '1px solid var(--warning, #d97706)',
              color: 'var(--warning, #d97706)', whiteSpace: 'nowrap',
            }}
          >
            ⚡ {highVacancyCount} high-vacancy
          </span>
        )}

        <div style={{ flex: 1 }} />

        {!collapsed && (
          <>
            <button type="button" style={miniBtn} onClick={() => shiftWindow(-1)} title="Earlier">
              <IconSystem.chevronLeft size={12} />
            </button>
            {/* Once an agent has paged forward, getting home must not mean paging back. */}
            <button
              type="button"
              style={{ ...miniBtn, opacity: isToday ? 0.45 : 1 }}
              onClick={() => setFrom(todayKey())}
              disabled={isToday}
              title="Back to today"
            >
              Today
            </button>
            <button type="button" style={miniBtn} onClick={() => shiftWindow(1)} title="Later">
              <IconSystem.chevronRight size={12} />
            </button>

            <div style={{ display: 'inline-flex', borderRadius: '0.375rem', overflow: 'hidden', border: '1px solid var(--border-color)' }}>
              {HORIZONS.map((h) => (
                <button
                  key={h}
                  type="button"
                  onClick={() => setHorizon(h)}
                  style={{
                    padding: '0.15rem 0.5rem', border: 'none', cursor: 'pointer',
                    fontSize: '0.7rem', fontWeight: 700, fontFamily: 'inherit',
                    background: horizon === h ? 'var(--primary)' : 'var(--bg-primary)',
                    color: horizon === h ? '#fff' : 'var(--text-secondary)',
                  }}
                  title={`${h}-day horizon`}
                >
                  {h}
                </button>
              ))}
            </div>

            <button type="button" style={miniBtn} onClick={load} title="Refresh the inventory position">
              <IconSystem.refresh size={12} />
            </button>
          </>
        )}
      </div>

      {!collapsed && (
        <>
          {!propertyId ? (
            <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', padding: '0.5rem 0' }}>
              Select a property in the filter panel to see unsold inventory.
            </div>
          ) : error ? (
            <div className="form-error-message" style={{ margin: '0.25rem 0' }}>{error}</div>
          ) : (
            <div
              style={{
                display: 'flex', gap: '0.3rem', overflowX: 'auto',
                paddingBottom: '0.2rem', opacity: loading ? 0.55 : 1,
                transition: 'opacity 0.15s ease',
              }}
            >
              {loading && days.length === 0 && (
                <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', padding: '0.5rem 0' }}>
                  Loading inventory position…
                </div>
              )}

              {days.map((d) => (
                <OccupancyDayCell
                  key={d.date}
                  day={d}
                  selected={d.date === selectedDate}
                  onSelect={onSelectDate}
                />
              ))}
            </div>
          )}

          <div style={{ fontSize: '0.6rem', color: 'var(--text-muted)', paddingBottom: '0.2rem' }}>
            Free rooms of total · bar &amp; colour = free % · footer{' '}
            <b>↑ arriving · staying over · ↓ departing</b> (bookings, not rooms)
          </div>
        </>
      )}
    </div>
  );
};

export default OccupancyStrip;
